<template>
  <div
    aria-label="Gestion des bons de remboursement logistiques"
    role="region"
  >
    <!-- Enhanced Filters Section -->
    <b-card class="mb-3">
      <b-card-header>
        <h5 class="mb-0">
          <feather-icon
            icon="FilterIcon"
            class="mr-1"
            aria-hidden="true"
          />
          Filtres de recherche des bons de remboursement
        </h5>
      </b-card-header>
      <b-card-body>
        <b-row class="mt-1">
          <b-col
            v-if="user.role === 'admin'"
            md="3"
          >
            <b-form-group
              label="Fournisseur logistique"
              label-for="supplier"
              aria-label="Filtrer par fournisseur logistique"
            >
              <v-select
                v-model="filters.supplier"
                :clearable="true"
                placeholder="Sélectionner un fournisseur logistique..."
                aria-label="Liste des fournisseurs logistiques"
                label="username"
                :options="suppliers"
                @input="applyFilters"
              />
            </b-form-group>
          </b-col>

          <b-col
            v-if="user.role === 'admin'"
            md="3"
          >
            <b-form-group
              label="Dépôt logistique"
              label-for="repository"
              aria-label="Filtrer par dépôt logistique"
            >
              <v-select
                v-model="filters.repository"
                :clearable="true"
                placeholder="Sélectionner un dépôt logistique..."
                aria-label="Liste des dépôts logistiques"
                label="name"
                :options="repositories"
                @input="applyFilters"
              />
            </b-form-group>
          </b-col>

          <b-col md="3">
            <b-form-group
              label="Date de début"
              label-for="dateFrom"
              aria-label="Filtrer par date de début"
            >
              <b-form-datepicker
                v-model="filters.dateFrom"
                v-bind="datePickerConfig"
                placeholder="Date de début"
                reset-button
                close-button
                @input="applyFilters"
              />
            </b-form-group>
          </b-col>

          <b-col md="3">
            <b-form-group
              label="Date de fin"
              label-for="dateTo"
              aria-label="Filtrer par date de fin"
            >
              <b-form-datepicker
                v-model="filters.dateTo"
                v-bind="datePickerConfig"
                placeholder="Date de fin"
                reset-button
                close-button
                @input="applyFilters"
              />
            </b-form-group>
          </b-col>

          <b-col md="3">
            <b-form-group
              label="Prix minimum (TND)"
              label-for="priceMin"
              aria-label="Filtrer par prix minimum"
            >
              <b-form-input
                v-model="filters.priceMin"
                type="number"
                step="0.001"
                placeholder="0.000"
                @input="applyFilters"
              />
            </b-form-group>
          </b-col>

          <b-col md="3">
            <b-form-group
              label="Prix maximum (TND)"
              label-for="priceMax"
              aria-label="Filtrer par prix maximum"
            >
              <b-form-input
                v-model="filters.priceMax"
                type="number"
                step="0.001"
                placeholder="0.000"
                @input="applyFilters"
              />
            </b-form-group>
          </b-col>
        </b-row>

        <!-- Reset Filters Button Row -->
        <b-row class="mt-2">
          <b-col class="d-flex justify-content-end">
            <b-button
              variant="outline-secondary"
              @click="clearFilters"
            >
              <feather-icon
                icon="XIcon"
                class="mr-1"
              />
              Effacer les filtres
              <span class="sr-only">Réinitialiser tous les filtres de recherche</span>
            </b-button>
          </b-col>
        </b-row>
      </b-card-body>
    </b-card>

    <!-- Action Buttons -->
    <b-row class="mb-3">
      <b-col class="d-flex justify-content-end">
        <b-button
          v-if="user.role === 'admin' || user.role === 'manager'"
          :to="{ name: 'bons-remboursement-add' }"
          variant="gradient-primary"
          class="ml-1"
          aria-label="Créer un nouveau bon de remboursement logistique"
        >
          <feather-icon
            icon="PlusIcon"
            aria-hidden="true"
          />
          <span class="ml-1">Ajouter un bon de remboursement</span>
        </b-button>
      </b-col>
    </b-row>

    <!-- Loading State -->
    <div
      v-if="isLoading"
      class="text-center mb-2"
    >
      <b-spinner
        variant="primary"
        label="Chargement des bons de remboursement"
      />
      <p class="mt-2">
        Chargement des bons de remboursement...
      </p>
    </div>

    <!-- Empty State - No Data at All -->
    <b-card
      v-else-if="!isLoading && returnVouchers.length === 0 && !hasActiveFilters"
      class="text-center"
    >
      <b-card-body class="py-5">
        <feather-icon
          icon="PackageIcon"
          size="48"
          class="text-muted mb-3"
          aria-hidden="true"
        />
        <h4 class="text-muted">
          Aucun bon de remboursement
        </h4>
        <p class="text-muted mb-3">
          Il n'y a pas encore de bons de remboursement dans le système.
        </p>
        <b-button
          v-if="user.role === 'admin' || user.role === 'manager'"
          :to="{ name: 'bons-remboursement-add' }"
          variant="primary"
        >
          <feather-icon
            icon="PlusIcon"
            class="mr-1"
          />
          Créer le premier bon de remboursement
        </b-button>
      </b-card-body>
    </b-card>

    <!-- Empty State - No Results for Filters -->
    <b-card
      v-else-if="!isLoading && returnVouchers.length === 0 && hasActiveFilters"
      class="text-center"
    >
      <b-card-body class="py-5">
        <feather-icon
          icon="SearchIcon"
          size="48"
          class="text-muted mb-3"
          aria-hidden="true"
        />
        <h4 class="text-muted">
          Aucun résultat trouvé
        </h4>
        <p class="text-muted mb-3">
          Aucun bon de remboursement ne correspond aux critères de recherche sélectionnés.
          Essayez de modifier vos filtres pour élargir votre recherche.
        </p>
        <b-button
          variant="outline-primary"
          @click="clearFilters"
        >
          <feather-icon
            icon="RefreshCwIcon"
            class="mr-1"
          />
          Réinitialiser les filtres
        </b-button>
      </b-card-body>
    </b-card>

    <!-- Data Table -->
    <vue-good-table
      v-else
      :columns="tableColumns"
      :rows="returnVouchers"
      :search-options="{ enabled: true, externalQuery: searchTerm }"
      :pagination-options="{ enabled: true, perPage: pageLength }"
      aria-label="Tableau des bons de remboursement logistiques"
      role="table"
    >
      <template
        slot="table-row"
        slot-scope="props"
      >
        <div v-if="props.column.field === 'id'">
          <qrcode-vue
            :value="props.row.id.toString()"
            :size="size"
            level="H"
          />
          <p>{{ props.row.id }}</p>
        </div>

        <span v-else-if="props.column.field === 'is_validated'">
          <b-badge :variant="props.row.is_validated ? 'success' : 'warning'">
            {{ props.row.is_validated ? 'Validé' : 'En attente' }}
          </b-badge>
        </span>

        <span v-else-if="props.column.field === 'total_price'">
          <strong>{{ formatPrice(props.row.total_price) }} TND</strong>
        </span>

        <span v-else-if="props.column.field === 'total_return_price'">
          <strong class="text-danger">{{ formatPrice(props.row.total_return_price) }} TND</strong>
        </span>

        <span v-else-if="props.column.field === 'action'">
          <router-link
            class="text-info"
            :to="{ name: 'bon-a-rembourser-show', params: { id: props.row.id } }"
          >
            <feather-icon
              :id="`invoice-row-${props.row.id}-show-icon`"
              icon="EyeIcon"
              class="cursor-pointer mr-1"
              size="16"
            />
          </router-link>
          <b-tooltip
            title="Voir les détails"
            :target="`invoice-row-${props.row.id}-show-icon`"
          />
        </span>

        <span v-else>
          {{ props.formattedRow[props.column.field] }}
        </span>
      </template>

      <!-- pagination -->
      <template
        slot="pagination-bottom"
        slot-scope="props"
      >
        <div class="d-flex justify-content-between flex-wrap">
          <div class="d-flex align-items-center mb-0 mt-1">
            <span class="text-nowrap">Affichage 1 à</span>
            <b-form-select
              v-model="pageLength"
              :options="['50','100','500']"
              class="mx-1"
              aria-label="Nombre de lignes par page"
              @input="(value) => props.perPageChanged({ currentPerPage: value })"
            />
            <span class="text-nowrap">de {{ count }} entrées</span>
          </div>

          <div>
            <b-pagination
              v-model="currentPage"
              :total-rows="count"
              :per-page="pageLength"
              first-number
              last-number
              align="right"
              prev-class="prev-item"
              next-class="next-item"
              class="mt-1 mb-0"
              @input="pageNext"
            >
              <template #prev-text>
                <feather-icon
                  icon="ChevronLeftIcon"
                  size="18"
                />
              </template>
              <template #next-text>
                <feather-icon
                  icon="ChevronRightIcon"
                  size="18"
                />
              </template>
            </b-pagination>
          </div>
        </div>
      </template>
    </vue-good-table>
  </div>
</template>

<script>
import {
  BPagination,
  BFormGroup,
  BFormSelect,
  BButton,
  BFormInput,
  BSpinner,
  BTooltip,
  BCol,
  BRow,
  BCard,
  BCardHeader,
  BCardBody,
  BBadge,
  BFormDatepicker,
} from 'bootstrap-vue'
import { VueGoodTable } from 'vue-good-table'
import axios from 'axios'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import QrcodeVue from 'qrcode.vue'
import vSelect from 'vue-select'
import storeAuth from '@/store/store'

export default {
  components: {
    VueGoodTable,
    BPagination,
    BFormGroup,
    BFormSelect,
    BButton,
    BSpinner,
    BTooltip,
    QrcodeVue,
    vSelect,
    BCol,
    BRow,
    BCard,
    BCardHeader,
    BCardBody,
    BBadge,
    BFormInput,
    BFormDatepicker,
  },
  data() {
    return {
      size: 40,
      isLoading: false,
      pageLength: 100,
      columns: [
        {
          label: 'Reference',
          field: 'id',
        },
        {
          label: 'Fournisseur',
          field: 'supplier.username',
        },
        {
          label: 'Nombre de colis',
          field: 'orders_count',
        },
        {
          label: 'Prix total (TND)',
          field: 'total_price',
        },
        {
          label: 'Net A payer (TND)',
          field: 'net_payment',
        },
        {
          label: 'Date de création',
          field: 'created_at',
          formatFn: this.formatFn,
        },
        {
          label: 'Action',
          field: 'action',
        },
      ],
      returnVouchers: [],
      searchTerm: '',
      currentPage: 1,
      count: 0,
      suppliers: [],
      repositories: [],
      filters: {
        supplier: null,
        repository: null,
        dateFrom: '',
        dateTo: '',
        priceMin: '',
        priceMax: '',
      },
      user: {},
      datePickerConfig: {
        locale: 'fr-FR',
        startWeekday: 1,
        labelHelp: 'Utilisez les flèches pour naviguer',
        labelPrevYear: 'Année précédente',
        labelPrevMonth: 'Mois précédent',
        labelCurrentMonth: 'Mois actuel',
        labelNextMonth: 'Mois suivant',
        labelNextYear: 'Année suivante',
        labelToday: 'Aujourd\'hui',
        labelSelected: 'Date sélectionnée',
        labelNoDateSelected: 'Aucune date sélectionnée',
      },
    }
  },
  computed: {
    tableColumns() {
      if (this.user.role === 'admin') {
        return this.columns
      }
      return this.columns.filter(column => !['supplier.username', 'repository'].includes(column.field))
    },
    hasActiveFilters() {
      return Object.values(this.filters).some(filter => filter !== null && filter !== '' && filter !== undefined)
    },
  },
  created() {
    this.user = storeAuth.state.user
    this.getReturnVouchers(1)
    if (this.user.role === 'admin') {
      this.getSuppliers()
      this.getRepositories()
    }
  },
  methods: {
    async getSuppliers() {
      try {
        const { data } = await axios.get('api/auth/users/suppliers/')
        this.suppliers = Array.isArray(data) ? data : []
      } catch (error) {
        console.error('Error fetching suppliers:', error)
      }
    },
    async getRepositories() {
      try {
        const response = await axios.get('api/repositories/')
        this.repositories = response.data.results || response.data
      } catch (error) {
        console.error('Error fetching repositories:', error)
      }
    },
    pageNext(currentPage) {
      this.getReturnVouchers(currentPage)
    },
    formatFn(date) {
      if (!date) return null
      const [year, month, day] = date.split('-')
      const day1 = day.substr(0, 2)
      const h = date.slice(11, 19)
      return `${year}-${month.padStart(2, '0')}-${day1.padStart(2, '0')} | ${h}`
    },
    formatPrice(price) {
      return price ? price.toFixed(3) : '0.000'
    },
    applyFilters() {
      this.currentPage = 1
      this.getReturnVouchers(1)
    },
    clearFilters() {
      this.filters = {
        supplier: null,
        repository: null,
        dateFrom: '',
        dateTo: '',
        priceMin: '',
        priceMax: '',
      }
      this.currentPage = 1
      this.getReturnVouchers(1)
    },
    buildFilterParams() {
      const params = { p: this.currentPage }

      if (this.filters.supplier) {
        params.supplier = this.filters.supplier.id
      }
      if (this.filters.repository) {
        params.repository = this.filters.repository.id
      }
      if (this.filters.dateFrom) {
        // Format date for Django filter with created_at__gte
        params.created_at__gte = this.filters.dateFrom
      }
      if (this.filters.dateTo) {
        // Format date for Django filter with created_at__lte
        params.created_at__lte = this.filters.dateTo
      }
      if (this.filters.priceMin) {
        // Format price for Django filter with total_price__gte
        params.total_price__gte = this.filters.priceMin
      }
      if (this.filters.priceMax) {
        // Format price for Django filter with total_price__lte
        params.total_price__lte = this.filters.priceMax
      }

      return params
    },
    async getReturnVouchers(currentPage) {
      this.isLoading = true
      try {
        const params = this.buildFilterParams()
        params.p = currentPage

        const { data } = await axios.get('api/return-vouchers/reimbursed/', { params })
        this.returnVouchers = data.results
        this.count = data.count
      } catch (error) {
        this.showToast('danger', 'top-right', 'Erreur lors du chargement des données')
      } finally {
        this.isLoading = false
      }
    },
    showToast(variant, position, text) {
      this.$toast(
        {
          component: ToastificationContent,
          props: {
            title: 'Notification',
            icon: 'InfoIcon',
            text,
            variant,
          },
        },
        {
          position,
        },
      )
    },
    clearDateFilter(field) {
      this.filters[field] = null
      this.applyFilters()
    },
  },
}
</script>

<style lang="scss">
@import '@core/scss/vue/libs/vue-select.scss';
@import '@core/scss/vue/libs/vue-good-table.scss';
</style>
